// The Clubs & Organizers tab ("Klubber og arrangører"): the organizers this
// arbiter belongs to or is invited to, one organizer's page (its arbiters, its
// details, its public tournaments), and the register / edit dialog.
//
// Data access is in js/services/organizers.js; every decision that needs no
// DOM is in js/utils/organizer-logic.js, under test. This file is rendering and
// event wiring only — the same split as the Player Databases tab.

import { escapeHtml } from '../utils/formatting.js';
import { isOfflineError } from '../utils/offline-guards.js';
import * as Orgs from '../services/organizers.js';
import { syncTopHeader } from './top-header.js';
import {
    ORGANIZER_TYPE_LABELS, organizerInitials, displayWebsite, validateOrganizerForm,
    roleIn, rosterRows, memberCount, wouldLeaveNoAdmin, inviteProblem, searchOrganizers,
    groupMyOrganizers, normEmail
} from '../utils/organizer-logic.js';

const $ = (id) => document.getElementById(id);

let organizers = [];      // listMyOrganizers(): profile + roster + myRole
let active = null;        // the organizer whose page is open
let editing = null;       // the organizer in the edit dialog, or null when registering
let draftLogoUrl = '';    // the logo the dialog will save
let allForSearch = null;  // the whole public register, for "is it already registered?"

function show(el, visible) {
    if (el) el.style.display = visible ? '' : 'none';
}

function openModal(id) {
    const m = $(id);
    if (m) m.classList.add('active');
}

function closeModal(id) {
    const m = $(id);
    if (m) m.classList.remove('active');
}

function me() {
    const user = window.authApp && window.authApp.getCurrentUser ? window.authApp.getCurrentUser() : null;
    return user ? normEmail(user.email) : '';
}

function errorText(err, fallback) {
    if (isOfflineError(err)) return tr('You appear to be offline. Try again when you are connected.');
    const msg = err && err.message ? String(err.message) : '';
    // Messages this feature throws are English source strings; anything else
    // (a raw Firebase error) is not something to show a person.
    if (msg && !/^(FirebaseError|Missing or insufficient permissions)/i.test(msg) && !/permission/i.test(msg)) return tr(msg);
    return tr(fallback);
}

/** The logo, or the initials on a tint. `size` is '', 'lg' or 'sm'. */
export function organizerMarkHtml(org, size = '') {
    const cls = 'org-mark' + (size ? ' org-mark-' + size : '');
    // https only — plus plain http from the local Storage emulator, which is
    // what a logo uploaded while testing against `firebase emulators:start` gets.
    const logo = org && org.logoUrl && /^(https:\/\/|http:\/\/(127\.0\.0\.1|localhost)[:/])/i.test(org.logoUrl) ? org.logoUrl : '';
    return logo
        ? `<span class="${cls}"><img src="${escapeHtml(logo)}" alt="" loading="lazy"></span>`
        : `<span class="${cls}" aria-hidden="true">${escapeHtml(organizerInitials(org))}</span>`;
}

function verifiedBadge(org) {
    return org && org.verified
        ? `<span class="status-badge active" title="${escapeHtml(tr('Checked by Zugzwang'))}"><i class="ri-verified-badge-line"></i> ${escapeHtml(tr('Verified'))}</span>`
        : `<span class="status-badge completed" title="${escapeHtml(tr('Works as normal while it waits. Zugzwang checks new organizers and marks them as verified.'))}">${escapeHtml(tr('Awaiting verification'))}</span>`;
}

function roleBadge(role) {
    return role === 'admin'
        ? `<span class="status-badge upcoming">${escapeHtml(tr('Administrator'))}</span>`
        : `<span class="status-badge muted">${escapeHtml(tr('Arbiter'))}</span>`;
}

function typeAndPlace(org) {
    const parts = [tr(ORGANIZER_TYPE_LABELS[org.type] || 'Club')];
    if (org.city) parts.push(org.city);
    if (org.federation) parts.push(org.federation);
    return parts.join(' · ');
}

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------

export async function renderOrganizersPage() {
    try {
        organizers = await Orgs.listMyOrganizers({ fresh: true });
    } catch (e) {
        console.error('Could not load organizers:', e);
        organizers = [];
    }
    if (active) {
        const fresh = organizers.find(o => o.id === active.id);
        if (fresh && (fresh.myRole === 'admin' || fresh.myRole === 'arbiter')) {
            active = fresh;
            renderDetail();
            return;
        }
        showListPane();
    }
    renderCards();
}

function showListPane() {
    active = null;
    show($('org-list-pane'), true);
    show($('org-detail-pane'), false);
    // Each pane has its own page title, and the top panel only re-checks which
    // one is visible when told to (js/ui/top-header.js).
    syncTopHeader();
}

function renderCards() {
    const container = $('org-cards');
    if (!container) return;

    if (organizers.length === 0) {
        container.innerHTML = `
            <div class="card glass" style="grid-column: 1 / -1; padding: 3rem; text-align: center; color: var(--text-muted);">
                <i class="ri-community-line" style="font-size: 2.5rem; display: block; margin-bottom: 1rem; opacity: 0.5;"></i>
                <p style="margin: 0 0 0.5rem;">${escapeHtml(tr('You are not a member of any club or organizer yet.'))}</p>
                <p style="margin: 0;">${escapeHtml(tr('Register the one you run tournaments for, or ask one of its admins to invite you.'))}</p>
            </div>`;
        return;
    }

    const { invitations, memberships } = groupMyOrganizers(organizers);
    const withHeadings = invitations.length > 0;
    const groups = [
        { title: tr('Invitations'), items: invitations },
        { title: tr('My Organizers'), items: memberships }
    ].filter(g => g.items.length > 0);

    container.innerHTML = groups.map(g =>
        (withHeadings ? `<h3 class="db-registry-heading">${escapeHtml(g.title)}</h3>` : '') +
        g.items.map(renderCard).join('')
    ).join('');
}

function renderCard(o) {
    const head = `
        <div class="org-card-head">
            ${organizerMarkHtml(o)}
            <div style="min-width: 0;">
                <h3>${escapeHtml(o.name || tr('Untitled'))}</h3>
                <div class="page-subtitle" style="margin: 0; font-size: 0.85rem;">${escapeHtml(typeAndPlace(o))}</div>
            </div>
        </div>`;

    if (o.myRole === 'pendingAdmin' || o.myRole === 'pendingArbiter') {
        const as = o.myRole === 'pendingAdmin' ? tr('Administrator') : tr('Arbiter');
        return `
        <div class="card glass db-card db-card-pending" data-org-id="${escapeHtml(o.id)}">
            <div class="card-body" style="padding: 1.5rem;">
                ${head}
                <p style="margin: 0.85rem 0 1rem; color: var(--text-muted); font-size: 0.9rem;">${escapeHtml(tr('You are invited to join as {role}.', { role: as.toLocaleLowerCase() }))}</p>
                <div style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
                    <button type="button" class="btn-primary" data-org-answer="accept"><i class="ri-check-line"></i> ${escapeHtml(tr('Accept'))}</button>
                    <button type="button" class="btn-outline" data-org-answer="decline">${escapeHtml(tr('Decline'))}</button>
                </div>
            </div>
        </div>`;
    }

    const n = memberCount(o.roster);
    return `
        <div class="card glass db-card" data-org-id="${escapeHtml(o.id)}" style="cursor: pointer;" tabindex="0" role="button">
            <div class="card-body" style="padding: 1.5rem;">
                ${head}
                <div class="org-badges">${roleBadge(o.myRole)}${verifiedBadge(o)}</div>
                <div class="org-card-meta">
                    <span><i class="ri-user-star-line"></i> ${escapeHtml(tr(n === 1 ? '{n} arbiter' : '{n} arbiters', { n }))}</span>
                </div>
            </div>
        </div>`;
}

async function onCardsClick(e) {
    const card = e.target.closest('[data-org-id]');
    if (!card) return;
    const org = organizers.find(o => o.id === card.dataset.orgId);
    if (!org) return;

    const answer = e.target.closest('[data-org-answer]');
    if (answer) {
        await answerInvite(org, answer.dataset.orgAnswer === 'accept', answer);
        return;
    }
    if (org.myRole === 'admin' || org.myRole === 'arbiter') openOrganizer(org);
}

async function answerInvite(org, accept, btn) {
    if (btn) btn.disabled = true;
    try {
        await Orgs.answerOrganizerInvite(org.id, accept);
        window.Dialog.success(
            accept
                ? tr('You are now a member of {name}. You can choose it when you create a tournament.', { name: org.name })
                : tr('Invitation declined.'),
            accept ? tr('Welcome') : tr('Declined'));
        await renderOrganizersPage();
    } catch (err) {
        console.error('Answering the organizer invitation failed:', err);
        if (btn) btn.disabled = false;
        window.Dialog.error(errorText(err, 'This invitation could not be answered. It may have been withdrawn.'), tr('Error'));
    }
}

// Called by the notification bell (js/app.js), which has its own buttons.
export async function answerOrganizerInviteFromNotification(organizerId, accept) {
    const org = await Orgs.answerOrganizerInvite(organizerId, accept);
    const onPage = document.getElementById('organizers');
    if (onPage && onPage.classList.contains('active')) renderOrganizersPage();
    return org;
}

// ---------------------------------------------------------------------------
// One organizer's page
// ---------------------------------------------------------------------------

function openOrganizer(org) {
    active = org;
    show($('org-list-pane'), false);
    show($('org-detail-pane'), true);
    renderDetail();
    syncTopHeader();
    const container = document.querySelector('.view-container');
    if (container) container.scrollTop = 0;
}

function renderDetail() {
    if (!active) return;
    const isAdmin = active.myRole === 'admin';

    $('org-detail-mark').outerHTML = organizerMarkHtml(active, 'lg').replace('class="', 'id="org-detail-mark" class="');
    $('org-detail-name').textContent = active.name || tr('Untitled');
    $('org-detail-subtitle').innerHTML = `${escapeHtml(typeAndPlace(active))} &nbsp;${verifiedBadge(active)}`;

    show($('btn-org-edit'), isAdmin);
    show($('btn-org-leave'), true);
    show($('org-invite-controls'), isAdmin);

    renderMembers();
    renderFacts();
    renderTournaments();
}

function renderMembers() {
    const listEl = $('org-members-list');
    if (!listEl || !active) return;
    const isAdmin = active.myRole === 'admin';
    const rows = rosterRows(active.roster, me());
    const n = memberCount(active.roster);
    $('org-members-count').textContent = tr(n === 1 ? '{n} arbiter' : '{n} arbiters', { n });

    listEl.innerHTML = rows.map(r => {
        const who = `
            <div class="org-member-who">
                <div class="org-member-name">${escapeHtml(r.name || r.email)}${r.isMe ? ` <span class="page-subtitle" style="font-size: 0.8rem; margin: 0;">(${escapeHtml(tr('you'))})</span>` : ''}</div>
                ${r.name ? `<div class="org-member-email">${escapeHtml(r.email)}</div>` : ''}
            </div>`;
        let actions;
        if (r.pending) {
            actions = `<span class="status-badge completed">${escapeHtml(r.role === 'admin' ? tr('Invited as admin') : tr('Invited'))}</span>`;
            if (isAdmin) {
                actions += `<button type="button" class="btn-icon-small btn-icon-danger" data-org-retract="${escapeHtml(r.email)}" title="${escapeHtml(tr('Withdraw invitation'))}" aria-label="${escapeHtml(tr('Withdraw invitation'))}"><i class="ri-close-circle-line"></i></button>`;
            }
        } else if (isAdmin) {
            actions = `
                <select class="form-control" data-org-role="${escapeHtml(r.email)}" aria-label="${escapeHtml(tr('Role'))}" style="width: auto; padding: 0.3rem 0.5rem; font-size: 0.8rem;">
                    <option value="admin"${r.role === 'admin' ? ' selected' : ''}>${escapeHtml(tr('Administrator'))}</option>
                    <option value="arbiter"${r.role === 'arbiter' ? ' selected' : ''}>${escapeHtml(tr('Arbiter'))}</option>
                </select>
                ${r.isMe ? '' : `<button type="button" class="btn-icon-small btn-icon-danger" data-org-remove="${escapeHtml(r.email)}" title="${escapeHtml(tr('Remove from organizer'))}" aria-label="${escapeHtml(tr('Remove from organizer'))}"><i class="ri-user-unfollow-line"></i></button>`}`;
        } else {
            actions = roleBadge(r.role);
        }
        return `<div class="org-member-row">${who}<div class="org-member-actions">${actions}</div></div>`;
    }).join('');
}

function renderFacts() {
    const el = $('org-detail-facts');
    if (!el || !active) return;
    const rows = [];
    if (active.email) rows.push([tr('Email'), escapeHtml(active.email)]);
    if (active.phone) rows.push([tr('Phone'), escapeHtml(active.phone)]);
    if (active.website) {
        rows.push([tr('Website'), `<a href="${escapeHtml(active.website)}" target="_blank" rel="noopener">${escapeHtml(displayWebsite(active.website))}</a>`]);
    }
    if (active.createdAt) rows.push([tr('Registered on'), escapeHtml(window.trDate ? window.trDate(String(active.createdAt).slice(0, 10)) : String(active.createdAt).slice(0, 10))]);
    el.innerHTML = rows.length
        ? rows.map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${v}</dd>`).join('')
        : `<dd style="grid-column: 1 / -1; color: var(--text-muted);">${escapeHtml(active.myRole === 'admin'
            ? tr('No contact details yet. Add them under Edit Details.')
            : tr('No contact details yet.'))}</dd>`;
}

async function renderTournaments() {
    const el = $('org-tournaments-list');
    if (!el || !active) return;
    const orgId = active.id;
    el.innerHTML = `<p class="page-subtitle" style="margin: 0;">${escapeHtml(tr('Loading...'))}</p>`;
    let list = [];
    try {
        list = await Orgs.listOrganizerPublicTournaments(orgId);
    } catch (e) {
        console.warn('Could not load the organizer\'s tournaments:', e);
    }
    if (!active || active.id !== orgId) return;
    if (list.length === 0) {
        el.innerHTML = `<p class="page-subtitle" style="margin: 0; font-size: 0.85rem;">${escapeHtml(tr('No public tournaments name this organizer yet. Choose it under Organizer when you create a tournament.'))}</p>`;
        return;
    }
    list.sort((a, b) => String(b.startDate || '').localeCompare(String(a.startDate || '')));
    el.innerHTML = list.map(t => `
        <div class="org-tournament-row" data-watch-id="${escapeHtml(t.id)}" role="button" tabindex="0">
            <span class="org-tournament-name">${escapeHtml(t.name || tr('Untitled'))}</span>
            <span class="org-tournament-date">${escapeHtml(t.startDate ? (window.trDate ? window.trDate(t.startDate) : t.startDate) : '')}</span>
        </div>`).join('');
}

async function refreshActive() {
    if (!active) return;
    Orgs.forgetMyOrganizers();
    await renderOrganizersPage();
}

async function invite() {
    if (!active) return;
    const input = $('org-invite-email');
    const btn = $('btn-org-invite');
    const email = (input.value || '').trim();
    const role = $('org-invite-role').value === 'admin' ? 'admin' : 'arbiter';
    const problem = inviteProblem(active.roster, email, me());
    if (problem) {
        window.Dialog.error(tr(problem), tr('Error'));
        return;
    }
    btn.disabled = true;
    try {
        const invited = await Orgs.inviteToOrganizer(active.id, email, role);
        input.value = '';
        await refreshActive();
        window.Dialog.success(tr('Invitation sent to {email}.', { email: invited }), tr('Success'));
    } catch (err) {
        console.error('Could not invite to the organizer:', err);
        window.Dialog.error(errorText(err, 'The invitation could not be sent. Please try again.'), tr('Error'));
    } finally {
        btn.disabled = false;
    }
}

async function onMembersClick(e) {
    if (!active) return;
    const retract = e.target.closest('[data-org-retract]');
    const remove = e.target.closest('[data-org-remove]');
    const btn = retract || remove;
    if (!btn) return;
    const email = btn.getAttribute(retract ? 'data-org-retract' : 'data-org-remove');

    const confirmed = retract
        ? await window.Dialog.confirm(
            tr('Withdraw the invitation to {email}?', { email }),
            tr('Withdraw invitation'), tr('Yes, Withdraw'), true, tr('Keep It'))
        : await window.Dialog.confirm(
            tr('Remove {email} from {name}? They can no longer choose it on new tournaments, and are told about it. Tournaments they already run keep the organizer.', { email, name: active.name }),
            tr('Remove from organizer'), tr('Yes, Remove'), true, tr('Cancel'));
    if (!confirmed) return;

    btn.disabled = true;
    try {
        if (retract) await Orgs.retractOrganizerInvite(active.id, email);
        else await Orgs.removeOrganizerMember(active.id, email);
        await refreshActive();
    } catch (err) {
        console.error('Could not change the organizer\'s arbiters:', err);
        window.Dialog.error(errorText(err, 'The change could not be saved. Please try again.'), tr('Error'));
        btn.disabled = false;
    }
}

async function onMembersChange(e) {
    const sel = e.target.closest('[data-org-role]');
    if (!sel || !active) return;
    const email = sel.getAttribute('data-org-role');
    const role = sel.value;
    const current = roleIn(active.roster, email);
    if (role === current) return;
    if (role !== 'admin' && wouldLeaveNoAdmin(active.roster, email)) {
        sel.value = current;
        window.Dialog.warn(tr('An organizer needs at least one admin. Make someone else an admin first.'), tr('Not Possible'));
        return;
    }
    sel.disabled = true;
    try {
        await Orgs.setOrganizerRole(active.id, email, role);
        await refreshActive();
    } catch (err) {
        console.error('Could not change the role:', err);
        sel.value = current;
        sel.disabled = false;
        window.Dialog.error(errorText(err, 'The change could not be saved. Please try again.'), tr('Error'));
    }
}

async function leave() {
    if (!active) return;
    if (wouldLeaveNoAdmin(active.roster, me())) {
        window.Dialog.warn(
            tr('You are the only admin. Make someone else an admin before you leave, or delete the organizer under Edit Details.'),
            tr('Not Possible'));
        return;
    }
    const confirmed = await window.Dialog.confirm(
        tr('Leave {name}? You can no longer choose it on new tournaments. Tournaments you already run keep the organizer.', { name: active.name }),
        tr('Leave Organizer'), tr('Yes, Leave'), true, tr('Cancel'));
    if (!confirmed) return;
    try {
        await Orgs.leaveOrganizer(active.id);
        showListPane();
        await renderOrganizersPage();
    } catch (err) {
        console.error('Could not leave the organizer:', err);
        window.Dialog.error(errorText(err, 'The change could not be saved. Please try again.'), tr('Error'));
    }
}

// ---------------------------------------------------------------------------
// The register / edit dialog
// ---------------------------------------------------------------------------

function renderLogoPreview() {
    const preview = $('org-logo-preview');
    if (!preview) return;
    const probe = { name: $('org-name').value, shortName: $('org-short-name').value, logoUrl: draftLogoUrl };
    preview.outerHTML = organizerMarkHtml(probe, 'lg').replace('class="', 'id="org-logo-preview" class="');
    show($('btn-org-logo-remove'), !!draftLogoUrl);
}

function openEditModal(org) {
    editing = org || null;
    const isNew = !editing;
    const o = editing || {};
    $('org-edit-title').textContent = isNew ? tr('Register Organizer') : tr('Edit Details');
    $('btn-org-save').textContent = isNew ? tr('Register Organizer') : tr('Save');
    $('org-name').value = o.name || '';
    $('org-short-name').value = o.shortName || '';
    $('org-type').value = o.type || 'club';
    $('org-city').value = o.city || '';
    $('org-federation').value = o.federation || (isNew ? 'NOR' : '');
    $('org-email').value = o.email || '';
    $('org-phone').value = o.phone || '';
    $('org-website').value = o.website || '';
    $('org-logo-file').value = '';
    draftLogoUrl = o.logoUrl || '';
    renderLogoPreview();

    show($('org-search-group'), isNew);
    $('org-search').value = '';
    $('org-search-results').innerHTML = '';
    show($('org-confirm-row'), isNew);
    $('org-confirm').checked = false;
    show($('org-danger'), !isNew && editing.myRole === 'admin');

    openModal('org-edit-modal');
    if (isNew) {
        // Load the register in the background for the search box.
        Orgs.listAllOrganizers().then(list => { allForSearch = list; }).catch(() => { allForSearch = []; });
    }
}

function renderSearch() {
    const out = $('org-search-results');
    if (!out) return;
    const typed = $('org-search').value;
    const hits = searchOrganizers(allForSearch || [], typed);
    if (!typed.trim() || typed.trim().length < 2) {
        out.innerHTML = '';
        return;
    }
    if (hits.length === 0) {
        out.innerHTML = `<p class="page-subtitle" style="margin: 0; font-size: 0.8rem;">${escapeHtml(tr('Not found. Fill in the details below to register it.'))}</p>`;
        return;
    }
    const mine = new Set(organizers.map(o => o.id));
    out.innerHTML = hits.map(o => `
        <div class="org-search-hit">
            ${organizerMarkHtml(o, 'sm')}
            <div class="org-search-hit-text">
                <div style="font-weight: 500; color: var(--text-main);">${escapeHtml(o.name)}</div>
                <div class="page-subtitle" style="margin: 0; font-size: 0.75rem;">${escapeHtml(typeAndPlace(o))}</div>
            </div>
            <div class="org-search-hit-note">${escapeHtml(mine.has(o.id)
                ? tr('You are already a member')
                : tr('Already registered. Ask one of its admins to invite you.'))}</div>
        </div>`).join('');
}

async function onLogoChosen() {
    const input = $('org-logo-file');
    const file = input.files && input.files[0];
    if (!file) return;
    const saveBtn = $('btn-org-save');
    saveBtn.disabled = true;
    try {
        draftLogoUrl = await Orgs.uploadOrganizerLogo(file);
        renderLogoPreview();
    } catch (err) {
        console.error('Logo upload failed:', err);
        window.Dialog.error(errorText(err, 'The image could not be uploaded. Please try again.'), tr('Error'));
    } finally {
        saveBtn.disabled = false;
        input.value = '';
    }
}

async function saveOrganizer(e) {
    e.preventDefault();
    const values = {
        name: $('org-name').value,
        shortName: $('org-short-name').value,
        type: $('org-type').value,
        city: $('org-city').value,
        federation: $('org-federation').value,
        email: $('org-email').value,
        phone: $('org-phone').value,
        website: $('org-website').value,
        logoUrl: draftLogoUrl
    };
    const check = validateOrganizerForm(values);
    if (!check.ok) {
        window.Dialog.error(check.errors.map(m => tr(m)).join('\n'), tr('Check the details'));
        return;
    }
    if (!editing && !$('org-confirm').checked) {
        window.Dialog.warn(tr('Tick the box to confirm that you may register this organizer.'), tr('Check the details'));
        return;
    }

    const btn = $('btn-org-save');
    btn.disabled = true;
    try {
        if (editing) {
            await Orgs.updateOrganizerProfile(editing.id, values);
            closeModal('org-edit-modal');
            await refreshActive();
        } else {
            const id = await Orgs.registerOrganizer(values);
            closeModal('org-edit-modal');
            await renderOrganizersPage();
            const created = organizers.find(o => o.id === id);
            if (created) openOrganizer(created);
            window.Dialog.success(
                tr('{name} is registered, and you are its admin. Invite the other arbiters from its page.', { name: check.profile.name }),
                tr('Organizer Registered'));
        }
    } catch (err) {
        console.error('Could not save the organizer:', err);
        window.Dialog.error(errorText(err, 'The organizer could not be saved. Please try again.'), tr('Error'));
    } finally {
        btn.disabled = false;
    }
}

async function deleteEditing() {
    if (!editing) return;
    const confirmed = await window.Dialog.confirm(
        tr('Delete {name}? It disappears from the register and its arbiters are told. Tournaments that name it keep the name. This cannot be undone.', { name: editing.name }),
        tr('Delete Organizer'), tr('Yes, Delete'), true, tr('Cancel'));
    if (!confirmed) return;
    try {
        await Orgs.deleteOrganizer(editing.id);
        closeModal('org-edit-modal');
        showListPane();
        await renderOrganizersPage();
    } catch (err) {
        console.error('Could not delete the organizer:', err);
        window.Dialog.error(errorText(err, 'The organizer could not be deleted. Please try again.'), tr('Error'));
    }
}

// ---------------------------------------------------------------------------
// Wiring
// ---------------------------------------------------------------------------

export function initOrganizersPage() {
    $('btn-register-organizer')?.addEventListener('click', () => openEditModal(null));
    $('btn-org-back')?.addEventListener('click', () => { showListPane(); renderCards(); });
    $('btn-org-edit')?.addEventListener('click', () => { if (active) openEditModal(active); });
    $('btn-org-leave')?.addEventListener('click', leave);
    $('btn-org-invite')?.addEventListener('click', invite);
    $('org-invite-email')?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); invite(); }
    });

    const cards = $('org-cards');
    if (cards) {
        cards.addEventListener('click', onCardsClick);
        cards.addEventListener('keydown', (e) => {
            if ((e.key === 'Enter' || e.key === ' ') && e.target.matches('[data-org-id][role="button"]')) {
                e.preventDefault();
                onCardsClick(e);
            }
        });
    }

    const members = $('org-members-list');
    if (members) {
        members.addEventListener('click', onMembersClick);
        members.addEventListener('change', onMembersChange);
    }

    const tournaments = $('org-tournaments-list');
    if (tournaments) {
        const watch = (e) => {
            const row = e.target.closest('[data-watch-id]');
            if (row && window.app && window.app.watchTournament) window.app.watchTournament(row.dataset.watchId);
        };
        tournaments.addEventListener('click', watch);
        tournaments.addEventListener('keydown', (e) => { if (e.key === 'Enter') watch(e); });
    }

    $('org-edit-form')?.addEventListener('submit', saveOrganizer);
    $('org-search')?.addEventListener('input', renderSearch);
    $('org-name')?.addEventListener('input', () => { if (!draftLogoUrl) renderLogoPreview(); });
    $('org-short-name')?.addEventListener('input', () => { if (!draftLogoUrl) renderLogoPreview(); });
    $('org-logo-file')?.addEventListener('change', onLogoChosen);
    $('btn-org-logo-remove')?.addEventListener('click', () => { draftLogoUrl = ''; renderLogoPreview(); });
    $('btn-org-delete')?.addEventListener('click', deleteEditing);
}
